import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { ShowToast } from "../../../components/toast";
import { api } from "../../../helpers/api";
import {
  getDefaultRouteForUser,
  isValidUserRole,
  type RoutingUser,
} from "../../../helpers/permissions";
import { setToken, setUser } from "../../../helpers/utils";

interface ImpersonationJwtPayload {
  userId?: string;
  type?: string;
  exp?: number;
}

/**
 * Best-effort client-side JWT decode. Used ONLY to extract the customer's
 * userId so we can call `api.getUserProfile(id)` and route on it — the
 * backend re-verifies the signature on every request (and `/auth/session`
 * derives its answer from the token, never from this payload), so a forged
 * or tampered payload here still fails server-side. Returns null on any
 * malformed token.
 */
function decodePayload(jwt: string): ImpersonationJwtPayload | null {
  try {
    const parts = jwt.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as ImpersonationJwtPayload;
  } catch {
    return null;
  }
}

/**
 * Auto-login landing page used by the admin CRM "Open App" / "View as
 * Customer" feature. The admin opens this URL in a new tab with the
 * impersonation JWT in the URL fragment:
 *
 *     /auto-login#token=<jwt>
 *
 * Fragment (not query) is deliberate — the token never enters Referer
 * headers or server access logs of this domain. We strip it from the
 * address bar immediately after reading so it can't be copy-shared.
 *
 * Token shape matches the backend's standard `{userId, type}` JWT, signed
 * with the same JWT_SECRET as `/auth/login` issues, so axios sees it as
 * a normal session token via the existing interceptor in `helpers/client`.
 *
 * Failure modes (expired token, missing token, unknown user, invalid role)
 * all bounce to `/login` with a clear toast — never a hung page.
 */
export default function AutoLogin() {
  const navigate = useNavigate();
  // React 18 StrictMode runs effects twice in dev. Without this guard the
  // second pass would re-read an already-stripped fragment, find no token,
  // and show "Auto-login link is invalid" on top of a successful login.
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const rawFragment = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : "";
    const params = new URLSearchParams(rawFragment);
    const token = params.get("token");

    // Wipe the fragment from the URL so a casual copy of the address
    // bar after auto-login doesn't leak the token.
    if (window.history.replaceState) {
      window.history.replaceState(null, "", window.location.pathname);
    }

    const payload = token ? decodePayload(token) : null;
    const customerId = payload?.userId;

    if (!token || !customerId) {
      ShowToast({
        message: "Auto-login link is invalid. Please log in normally.",
        type: "error",
      });
      navigate("/login", { replace: true });
      return;
    }

    setToken(token);

    void (async () => {
      try {
        // Mirror the blob Login.tsx persists — {...authResponse,
        // ...userProfile} — so PermissionGuard / Main / role-based routing and
        // the property switcher all see the same localStorage.user payload
        // they get from a normal login.
        //
        // `GET /users/:id` alone is NOT enough: it carries no linkedAccounts,
        // currentHotel or lastUsedHotelId. Those live only on the auth
        // response, which this flow never receives — it lands with a token
        // instead of logging in. Persisting the profile by itself left
        // useCurrentProperty() with an empty linkedAccounts array, so
        // PropertySwitcher collapsed to a single row and the customer's other
        // properties were invisible to the admin.
        //
        // `/auth/session` returns that blob WITHOUT a new accessToken, so the
        // short-lived, audit-logged impersonation token below stays in force.
        //
        // Sequential, not Promise.all: the response interceptor fires its own
        // "Session expired" toast and a delayed location.replace for EVERY
        // 401, so parallelising an expired link would double both on top of
        // this flow's own toast. One extra round-trip is free here — the
        // getOnboarding call below is already sequential.
        const sessionView = await api.getSession();
        const userResponse = await api.getUserProfile(customerId);
        setUser({ ...sessionView, ...userResponse });

        const storedUser = JSON.parse(
          localStorage.getItem("user") || "{}",
        ) as RoutingUser;
        const userRole = storedUser.type;

        if (!userRole || !isValidUserRole(userRole)) {
          ShowToast({
            message: "Invalid user role. Please log in normally.",
            type: "error",
          });
          navigate("/login", { replace: true });
          return;
        }

        try {
          const onboardingResponse = await api.getOnboarding(customerId);
          if (onboardingResponse?.isOnboardingCompleted) {
            navigate(getDefaultRouteForUser(storedUser), { replace: true });
          } else {
            navigate("/onboarding", { replace: true });
          }
        } catch {
          // Onboarding fetch failures shouldn't strand the admin on this
          // page — fall through to the account's default landing route.
          navigate(getDefaultRouteForUser(storedUser), { replace: true });
        }
      } catch {
        // Token rejected (expired, target inactive, signature invalid).
        // Clean up so the next render of /login is a fresh state.
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        ShowToast({
          message: "Auto-login link expired. Please log in normally.",
          type: "error",
        });
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate]);

  return null;
}
